import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Spinner,
  Stack,
  toast,
} from '@landler/component-library';
import { useQueryClient } from '@tanstack/react-query';
import { FC, PropsWithChildren } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import { OrganizationSummary, PatchOrganizationSummaryRequestBodyData } from '@/api/rest/resources/types/organization';
import { FormControl, FormField, FormItem, FormLabel, FormMessage, SuspenseBoundary, useFormField } from '@/components';
import { Logger } from '@/lib/logs/logger';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationSummary } from '@/pages/shared/hooks/useOrganizationSummary';
import {
  useCreateOrganizationSummary,
  useUpdateOrganizationSummary,
} from '@/pages/shared/hooks/useUpdateOrganizationSummary';
import { isNullish } from '@/utils/booleans';

export const UpdateOrganizationSummary = ({
  organizationId,
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: DialogProps['onOpenChange'];
  organizationId?: string;
}) => {
  const { t } = useTranslation();

  const onSuccess = () => {
    onOpenChange?.(!open);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='px-8 py-6' onClick={(e) => e.stopPropagation()} withXButton={false}>
        <DialogHeader>
          <DialogTitle>{t('global.organization.summary.form.title')}</DialogTitle>
        </DialogHeader>
        <SuspenseBoundary
          fallback={
            <Stack centerMain>
              <Spinner size={10} />
            </Stack>
          }
        >
          <SummaryForm organizationIdArg={organizationId} onSuccess={onSuccess} />
        </SuspenseBoundary>
      </DialogContent>
    </Dialog>
  );
};

export type SummaryFormFields = PatchOrganizationSummaryRequestBodyData;

const SummaryForm = ({ organizationIdArg, onSuccess }: { onSuccess?: () => void; organizationIdArg?: string }) => {
  const { t } = useTranslation();
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  const organizationSummary = useOrganizationSummary(organizationId).data;
  const summary = Object.keys(organizationSummary).length === 0 ? null : organizationSummary;

  const form = useForm<SummaryFormFields>({
    defaultValues: {
      summary: (summary as OrganizationSummary)?.summary ?? null,
    },
  });

  const queryClient = useQueryClient();

  const { submit: createSubmit, isSubmitting: createIsSubmitting } = useCreateOrganizationSummary();
  const { submit: updateSubmit, isSubmitting: updateIsSubmitting } = useUpdateOrganizationSummary();
  const submit = isNullish(summary) ? createSubmit : updateSubmit;
  const isSubmitting = isNullish(summary) ? createIsSubmitting : updateIsSubmitting;

  const onSubmit: SubmitHandler<SummaryFormFields> = async (inputs) => {
    try {
      form.clearErrors();
      await submit(organizationId, { ...(inputs as PatchOrganizationSummaryRequestBodyData) }, onSuccess);
      queryClient.invalidateQueries({
        queryKey: ['organization', { organization_id: organizationId }, 'summary'],
      });

      form.reset();
    } catch (error) {
      if (error instanceof Error) {
        Logger.error(error);
      }
      if (error instanceof RestApiBadRequestServerError && error.formErrors.length > 0) {
        error.formErrors.forEach((formError) => {
          form.setError(formError.field as keyof SummaryFormFields, {
            type: 'custom',
            message: formError.messages?.[0],
          });
        });
      } else {
        toast({
          title: t('global.ui.toast.errorToastFallbackTitle'),
          description: t('global.ui.toast.errorToastFallbackDescription'),
          type: 'error',
        });
      }
    }
  };
  const isFormPristine = !form.formState.isDirty;
  return (
    <FormProvider {...form}>
      <DialogBody>
        <form
          id='organization-summary-form'
          onSubmit={form.handleSubmit(onSubmit)}
          data-testid='organization-summary-form'
        >
          <fieldset disabled={isSubmitting}>
            <Stack className='gap-4'>
              <FormField
                control={form.control}
                name='summary'
                rules={{ required: true }}
                render={({ field: { value, ...field } }) => (
                  <FormItem className='flex flex-col gap-2'>
                    <CustomFormLabel>{t('global.organization.summary.form.fields.summary.label')} *</CustomFormLabel>
                    <FormControl>
                      <textarea
                        className={cn(
                          'flex min-h-[200px] w-full rounded-lg border border-neutral-black-12 p-4 placeholder:text-text-disabled',
                          'disabled:cursor-not-allowed disabled:border-text-disabled',
                        )}
                        placeholder={value ?? t('global.organization.summary.form.fields.summary.placeholder')}
                        value={value ?? ''}
                        {...field}
                      />
                    </FormControl>
                    <CustomFormMessage>{form.formState.errors.summary?.message}</CustomFormMessage>
                  </FormItem>
                )}
              />
            </Stack>
          </fieldset>
        </form>
      </DialogBody>
      <DialogFooter className='mt-6'>
        <DialogClose asChild>
          <Button variant='text' size='small' disabled={isSubmitting}>
            {t('global.ui.buttons.cancel')}
          </Button>
        </DialogClose>
        <Stack direction='row' spacing={4}>
          <Button
            form='organization-summary-form'
            size='small'
            type='submit'
            disabled={isSubmitting || isFormPristine}
            loading={isSubmitting}
          >
            {t('global.ui.buttons.save')}
          </Button>
        </Stack>
      </DialogFooter>
    </FormProvider>
  );
};

const CustomFormLabel: FC<PropsWithChildren> = ({ children }) => {
  const { error } = useFormField();

  return (
    <FormLabel
      className={cn('text-[.75rem] font-medium leading-4 text-text-secondary md:text-[.8125rem] md:leading-5', {
        'text-error': error,
      })}
    >
      {children}
    </FormLabel>
  );
};

const CustomFormMessage: FC<PropsWithChildren> = ({ children }) => (
  <FormMessage className='typography-inputLabel mt-0 text-error'>{children}</FormMessage>
);
